// Streaming resampler to 16 kHz mono PCM16. Box-filter decimation (each output
// sample averages the input samples that fall in its span): cheap, stable
// across buffer boundaries, and alias-safe enough for speech recognition.
// The server's batch path uses the same algorithm.

export const TARGET_RATE = 16_000

export class Resampler16k {
  private readonly ratio: number
  private pending: Float32Array = new Float32Array(0)
  /** Global index of pending[0] in the input stream. */
  private base = 0
  /** Output samples produced so far. */
  private produced = 0

  constructor(readonly inputRate: number) {
    if (!(inputRate >= 8000)) throw new Error(`Unsupported input sample rate ${inputRate}`)
    this.ratio = inputRate / TARGET_RATE
  }

  /** Feed float samples in [-1, 1]; returns the PCM16 samples now complete. */
  push(input: Float32Array): Int16Array {
    if (this.inputRate === TARGET_RATE) return toPcm16(input)
    const buf = new Float32Array(this.pending.length + input.length)
    buf.set(this.pending)
    buf.set(input, this.pending.length)
    const end = this.base + buf.length
    const out: number[] = []
    if (this.ratio > 1) {
      for (;;) {
        const s = Math.floor(this.produced * this.ratio)
        const e = Math.floor((this.produced + 1) * this.ratio)
        if (e > end) break
        let sum = 0
        for (let i = s; i < e; i++) sum += buf[i - this.base]
        out.push(e > s ? sum / (e - s) : 0)
        this.produced++
      }
    } else {
      for (;;) {
        const x = this.produced * this.ratio
        const i = Math.floor(x)
        if (i + 1 >= end) break
        const a = buf[i - this.base]
        const b = buf[i + 1 - this.base]
        out.push(a + (b - a) * (x - i))
        this.produced++
      }
    }
    // Keep everything from the start of the next output span.
    const keepFrom = Math.min(Math.floor(this.produced * this.ratio), end)
    this.pending = buf.slice(keepFrom - this.base)
    this.base = keepFrom
    return toPcm16(Float32Array.from(out))
  }
}

export function toPcm16(input: Float32Array): Int16Array {
  const out = new Int16Array(input.length)
  for (let i = 0; i < input.length; i++) {
    const s = Math.max(-1, Math.min(1, input[i]))
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff
  }
  return out
}
